import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Download, Copy, CheckCircle2, XCircle, Upload } from "lucide-react";
import { useSeo } from "../lib/useSeo";

const API = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = "https://web.aipromptshortcuts.app";

const BENEFITS = [
  ["Partner visibility", "Your logo and direct website link displayed within our Partner Network."],
  ["Shared audiences", "Introduce your organisation to AI Prompt Shortcuts visitors while introducing AI Prompt Shortcuts to your own website visitors."],
  ["Lifetime Partner Access", "Complimentary lifetime platform access for one nominated email address."],
  ["Partner recognition", "Permission to display the official AI Prompt Shortcuts Partner logo on your website."],
  ["No partnership fee", "Participation is based on reciprocal visibility rather than payment."],
];

const TERMS = [
  "I am authorised to apply on behalf of this company or organisation.",
  "I agree to display the AI Prompt Shortcuts Partner logo with a working clickable link to AI Prompt Shortcuts on our website for the duration of the partnership.",
  "I give AI Prompt Shortcuts permission to display our company name, submitted logo and clickable website link on the AI Prompt Shortcuts Partner page.",
  "I understand that complimentary Lifetime Partner Access applies to one nominated email address and remains subject to the Partner Programme Terms and platform Terms of Use.",
  "I understand that removal of the AI Prompt Shortcuts partner link may result in suspension or termination of the reciprocal partnership.",
];

const EMBED_CODE = `<a href="${SITE_URL}" target="_blank" rel="noopener">
  <img src="${SITE_URL}/assets/partner-badge.png" alt="Proud Partner of AI Prompt Shortcuts" width="180" />
</a>`;

const EMBED_CODE_DARK = `<a href="${SITE_URL}" target="_blank" rel="noopener">
  <img src="${SITE_URL}/assets/partner-badge-dark.png" alt="Proud Partner of AI Prompt Shortcuts" width="180" />
</a>`;

const EMPTY = { company_name: "", contact_person: "", job_title: "", business_email: "", website_url: "",
  country: "", industry: "", description: "", lifetime_email: "", partner_page_url: "" };

const inputCls = "w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50";

export default function BecomePartnerPage() {
  const [form, setForm] = useState(EMPTY);
  const [logoUrl, setLogoUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [checks, setChecks] = useState(TERMS.map(() => false));
  const [finalTerms, setFinalTerms] = useState(false);
  const [verify, setVerify] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const formRef = useRef(null);

  useSeo({
    title: "Become a Partner — Reciprocal Partner Programme",
    description: "Join the AI Prompt Shortcuts Partner Network. Free reciprocal visibility, shared audiences and complimentary Lifetime Partner Access. No partnership fee.",
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const allTicked = checks.every(Boolean) && finalTerms;
  const requiredFilled = Object.values(form).every((v) => v.trim()) && logoUrl;

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/api/partners/logo`, fd);
      setLogoUrl(r.data.url);
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Logo upload failed");
    } finally {
      setUploading(false);
    }
  };

  const runVerify = async () => {
    if (!/^https?:\/\//.test(form.partner_page_url)) { toast.error("Enter the full page URL starting with https://"); return; }
    setVerifying(true);
    try {
      const r = await axios.post(`${API}/api/partners/verify-link`, { url: form.partner_page_url });
      setVerify(r.data.verified);
    } catch { setVerify(false); }
    setVerifying(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!allTicked) { toast.error("Please accept all partnership terms"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/partners/apply`, { ...form, logo_url: logoUrl, terms_accepted: true });
      setDone(r.data.verified);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong — please try again");
    } finally {
      setBusy(false);
    }
  };

  const copyEmbed = () => { navigator.clipboard.writeText(EMBED_CODE); toast.success("Embed code copied"); };
  const copyEmbedDark = () => { navigator.clipboard.writeText(EMBED_CODE_DARK); toast.success("Dark embed code copied"); };

  if (done !== null) return (
    <div className="bg-[#FAFAF7] min-h-screen py-24 px-4 flex items-start justify-center" data-testid="partner-apply-success">
      <div className="bg-white border border-slate-200 rounded-2xl p-10 max-w-lg text-center shadow-sm">
        <CheckCircle2 className="mx-auto text-emerald-600" size={44} />
        <h1 className="font-display text-2xl font-extrabold mt-4">Application received</h1>
        <p className="text-slate-500 text-sm mt-3">
          {done
            ? "Your partner link was verified automatically. Your application is now awaiting final approval — once approved, your logo goes live and Lifetime Partner Access is activated automatically."
            : "We couldn't automatically confirm the partner link on your page, so your application has been flagged for a quick manual review. No further action is needed from you."}
        </p>
        <Link to="/partners" className="inline-block mt-6 text-emerald-700 font-bold text-sm hover:underline">View our Partners page →</Link>
      </div>
    </div>
  );

  return (
    <div className="bg-[#FAFAF7] min-h-screen py-14 lg:py-20 px-4 sm:px-6 lg:px-8" data-testid="become-partner-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center">
          <p className="font-display text-[#B8962E] font-bold text-xs sm:text-sm tracking-[0.16em] uppercase mb-3">Partner Programme</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">Become an AI Prompt Shortcuts Partner</h1>
          <p className="font-display font-bold text-slate-700 mt-4">Grow visibility. Share audiences. Build useful connections.</p>
          <p className="text-slate-500 mt-4 text-sm sm:text-base max-w-2xl mx-auto">
            AI Prompt Shortcuts welcomes technology companies, SaaS providers, AI platforms, educators, business service
            providers, professional organisations and other relevant companies to join our Partner &amp; Collaborator Network.
          </p>
          <p className="text-slate-500 mt-3 text-sm sm:text-base max-w-2xl mx-auto">
            Our partnership is based on a simple reciprocal arrangement: we display your clickable company logo and website
            link on our Partner page, and you display the AI Prompt Shortcuts logo and clickable website link on your website.
            <strong> There is no partnership fee.</strong> Approved partners also receive complimentary Lifetime Partner
            Access to AI Prompt Shortcuts for one nominated email address.
          </p>
          <button data-testid="become-partner-cta" onClick={() => formRef.current?.scrollIntoView({ behavior: "smooth" })}
            className="mt-7 bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-extrabold px-9 py-4 rounded-xl hover:brightness-105 active:scale-[0.98] transition-all">
            BECOME A PARTNER
          </button>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-14">
          {BENEFITS.map(([t, d]) => (
            <div key={t} className="bg-white border border-slate-200 rounded-2xl p-5">
              <h3 className="font-display font-bold text-slate-900">{t}</h3>
              <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>

        {/* PARTNER ASSETS */}
        <div className="mt-14 bg-[#0B0F0D] rounded-2xl p-6 sm:p-10" data-testid="partner-assets">
          <h2 className="font-display text-white font-extrabold text-xl sm:text-2xl">Step 1 — Add our partner logo to your website</h2>
          <p className="text-gray-400 text-sm mt-2">Before applying, place the AI Prompt Shortcuts partner logo on your website with a clickable link. Suggested wording: <span className="text-[#D4AF37] font-semibold">“Proud Partner of AI Prompt Shortcuts”</span></p>
          <div className="flex flex-col md:flex-row gap-6 mt-6 items-start">
            <div className="flex flex-col gap-4 shrink-0">
              <div className="bg-white rounded-xl p-5 flex flex-col items-center gap-3">
                <p className="text-slate-500 text-[10px] font-bold uppercase tracking-wider">For light websites</p>
                <img src="/assets/partner-badge.png" alt="Proud Partner of AI Prompt Shortcuts badge" className="h-16 object-contain" data-testid="partner-badge-preview" />
                <a href="/assets/partner-badge.png" download="ai-prompt-shortcuts-partner-badge.png" target="_blank" rel="noopener noreferrer"
                  data-testid="partner-logo-download"
                  className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors">
                  <Download size={13} /> Download light badge
                </a>
              </div>
              <div className="bg-[#121815] border border-white/10 rounded-xl p-5 flex flex-col items-center gap-3">
                <p className="text-gray-400 text-[10px] font-bold uppercase tracking-wider">For dark websites</p>
                <img src="/assets/partner-badge-dark.png" alt="Proud Partner of AI Prompt Shortcuts dark badge" className="h-16 object-contain" data-testid="partner-badge-dark-preview" />
                <a href="/assets/partner-badge-dark.png" download="ai-prompt-shortcuts-partner-badge-dark.png" target="_blank" rel="noopener noreferrer"
                  data-testid="partner-logo-dark-download"
                  className="inline-flex items-center gap-1.5 bg-white text-slate-900 text-xs font-bold px-4 py-2 rounded-lg hover:bg-emerald-100 transition-colors">
                  <Download size={13} /> Download dark badge
                </a>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-gray-300 text-xs font-bold uppercase tracking-wider">Link to</p>
              <p className="text-[#D4AF37] font-mono text-sm mt-1 break-all">{SITE_URL}</p>
              <p className="text-gray-300 text-xs font-bold uppercase tracking-wider mt-4">Embed code — light badge</p>
              <pre className="bg-black/50 border border-white/10 rounded-lg p-3 text-[11px] text-gray-300 mt-1.5 overflow-x-auto whitespace-pre-wrap">{EMBED_CODE}</pre>
              <button onClick={copyEmbed} data-testid="partner-embed-copy"
                className="mt-2 inline-flex items-center gap-1.5 bg-white/10 text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-white/20 transition-colors">
                <Copy size={13} /> Copy embed code
              </button>
              <p className="text-gray-300 text-xs font-bold uppercase tracking-wider mt-5">Embed code — dark badge</p>
              <pre className="bg-black/50 border border-white/10 rounded-lg p-3 text-[11px] text-gray-300 mt-1.5 overflow-x-auto whitespace-pre-wrap">{EMBED_CODE_DARK}</pre>
              <button onClick={copyEmbedDark} data-testid="partner-embed-dark-copy"
                className="mt-2 inline-flex items-center gap-1.5 bg-white/10 text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-white/20 transition-colors">
                <Copy size={13} /> Copy dark embed code
              </button>
            </div>
          </div>
        </div>

        {/* APPLICATION FORM */}
        <form ref={formRef} onSubmit={submit} className="mt-14 bg-white border border-slate-200 rounded-2xl p-6 sm:p-10" data-testid="partner-apply-form">
          <h2 className="font-display font-extrabold text-xl sm:text-2xl">Step 2 — Partner application</h2>
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            <input required data-testid="pf-company" className={inputCls} placeholder="Company / Organisation name *" value={form.company_name} onChange={set("company_name")} />
            <input required data-testid="pf-contact" className={inputCls} placeholder="Contact person *" value={form.contact_person} onChange={set("contact_person")} />
            <input required data-testid="pf-job" className={inputCls} placeholder="Job title *" value={form.job_title} onChange={set("job_title")} />
            <input required type="email" data-testid="pf-email" className={inputCls} placeholder="Business email *" value={form.business_email} onChange={set("business_email")} />
            <input required data-testid="pf-website" className={inputCls} placeholder="Website URL (https://…) *" value={form.website_url} onChange={set("website_url")} />
            <input required data-testid="pf-country" className={inputCls} placeholder="Country *" value={form.country} onChange={set("country")} />
            <input required data-testid="pf-industry" className={inputCls} placeholder="Industry / Category *" value={form.industry} onChange={set("industry")} />
            <input required type="email" data-testid="pf-lifetime-email" className={inputCls} placeholder="Nominated email for Lifetime Partner Access *" value={form.lifetime_email} onChange={set("lifetime_email")} />
          </div>
          <textarea required data-testid="pf-description" className={`${inputCls} mt-4`} rows={3} maxLength={600}
            placeholder="Short company description *" value={form.description} onChange={set("description")} />

          <div className="mt-4">
            <label className="text-sm font-bold text-slate-700">Company logo (PNG, JPG, WEBP or SVG) *</label>
            <div className="flex items-center gap-4 mt-2">
              <label className="inline-flex items-center gap-2 bg-slate-900 text-white text-sm font-bold px-5 py-2.5 rounded-xl cursor-pointer hover:bg-emerald-700 transition-colors">
                {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {logoUrl ? "Replace logo" : "Upload logo"}
                <input type="file" accept=".png,.jpg,.jpeg,.webp,.svg" className="hidden" onChange={uploadLogo} data-testid="pf-logo-input" />
              </label>
              {logoUrl && <img src={`${API}${logoUrl}`} alt="Uploaded logo preview" className="h-12 object-contain border border-slate-200 rounded-lg px-3 py-1 bg-white" data-testid="pf-logo-preview" />}
            </div>
          </div>

          <div className="mt-5">
            <label className="text-sm font-bold text-slate-700">Page on your website showing our partner logo &amp; link *</label>
            <div className="flex flex-col sm:flex-row gap-2 mt-2">
              <input required data-testid="pf-partner-page" className={inputCls} placeholder="https://yourcompany.com/partners"
                value={form.partner_page_url} onChange={(e) => { setForm({ ...form, partner_page_url: e.target.value }); setVerify(null); }} />
              <button type="button" onClick={runVerify} disabled={verifying} data-testid="pf-verify-btn"
                className="shrink-0 bg-slate-900 text-white text-sm font-bold px-5 py-3 rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-60">
                {verifying ? <Loader2 size={15} className="animate-spin inline" /> : "Verify link"}
              </button>
            </div>
            {verify === true && <p className="flex items-center gap-1.5 text-emerald-700 text-sm font-bold mt-2" data-testid="pf-verify-ok"><CheckCircle2 size={15} /> Partner Link Verified</p>}
            {verify === false && <p className="flex items-center gap-1.5 text-red-600 text-sm font-bold mt-2" data-testid="pf-verify-fail"><XCircle size={15} /> Partner Link Could Not Be Verified — you can still submit and we'll review it manually</p>}
          </div>

          <div className="mt-8 space-y-3">
            <h3 className="font-display font-bold text-slate-900">Partnership terms — all required</h3>
            {TERMS.map((t, i) => (
              <label key={i} className="flex items-start gap-3 text-sm text-slate-600 cursor-pointer">
                <input type="checkbox" checked={checks[i]} data-testid={`pf-term-${i}`}
                  onChange={(e) => setChecks(checks.map((c, j) => (j === i ? e.target.checked : c)))}
                  className="mt-0.5 h-4 w-4 accent-emerald-700 shrink-0" />
                {t}
              </label>
            ))}
            <label className="flex items-start gap-3 text-sm text-slate-600 cursor-pointer">
              <input type="checkbox" checked={finalTerms} data-testid="pf-term-final"
                onChange={(e) => setFinalTerms(e.target.checked)} className="mt-0.5 h-4 w-4 accent-emerald-700 shrink-0" />
              <span>I accept the <Link to="/partner-terms" target="_blank" className="text-emerald-700 font-semibold hover:underline">Partner Programme Terms</Link> and <Link to="/privacy" target="_blank" className="text-emerald-700 font-semibold hover:underline">Privacy Policy</Link>.</span>
            </label>
          </div>

          <button type="submit" disabled={!allTicked || !requiredFilled || busy} data-testid="pf-submit"
            className="mt-8 w-full bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-extrabold py-4 rounded-xl hover:brightness-105 active:scale-[0.99] transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
            {busy && <Loader2 size={16} className="animate-spin" />} SUBMIT PARTNER APPLICATION
          </button>
          {!allTicked && <p className="text-xs text-slate-400 text-center mt-2">Accept all terms above to enable submission.</p>}
        </form>
      </div>
    </div>
  );
}
